import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { isAuthenticated } from 'library/auth/authProvider';
import { authActions } from 'reduxStore/auth/slice';

/**
 * Ends the session when the provider has already dropped it, without waiting for a request.
 * Needed because pages hydrate per scope: clicking through a dead session issues zero requests.
 */
const SessionWatcher = () => {
	const dispatch = useDispatch();
	const { pathname } = useLocation();
	// One verdict per mount: the triggers overlap (a tab return fires visibility AND focus).
	const hasExpired = useRef(false);

	const verifySession = useCallback(async () => {
		if (hasExpired.current) return;
		// A hidden tab has nobody to expel, and returning to it re-fires this anyway.
		if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

		// ⚠️ Blind while the local access token is still in date — isAuthenticated() answers from it.
		// A session revoked inside that window belongs to superFetch's 401 guard, not here.
		const live = await isAuthenticated();
		if (live || hasExpired.current) return;

		hasExpired.current = true;
		dispatch(authActions.sessionExpired());
	}, [dispatch]);

	// Route change is a trigger, so this runs on every navigation — the "clicks another page" half.
	useEffect(() => {
		verifySession();
	}, [verifySession, pathname]);

	useEffect(() => {
		const onReturn = () => verifySession();
		document.addEventListener('visibilitychange', onReturn);
		window.addEventListener('focus', onReturn);
		return () => {
			document.removeEventListener('visibilitychange', onReturn);
			window.removeEventListener('focus', onReturn);
		};
	}, [verifySession]);

	return null;
};

export default SessionWatcher;
